import {CancelableListener, IsCancelled, IsProcessActions} from '@mintdata/mint-kit/impl/events/Events';
import * as React from 'react';

export enum KeyEventType
{
    KeyDown = 1,
    KeyUp = 2
}

export enum KeyCode
{
    MetaLeft = 91,
    MetaRight = 93,
    Shift = 16,
    Ctl = 17,
    Alt = 18,
    Left = 37,
    Up = 38,
    Right = 39,
    Down = 40,
    Esc = 27,
    Enter = 13,
    Backspace = 8,
    Tab = 9,
    Space = 32,
    F1 = 112,
    F2 = 113,
    F3 = 114,
    F4 = 115,
    F5 = 116,
    Delete = 127
}

export class KeyEvent
{
    public static ProcessActions:IsProcessActions = true;
    public static DoNotProcessActions:IsProcessActions = false;
    public static Cancel:IsCancelled = true;
    public static DoNotCancel:IsCancelled = false;

    private constructor(public readonly type:KeyEventType,
                        public readonly key:Key,
                        public readonly browserEvent:any)
    {
    }

    public static down(key:Key, browserEvent:any)
    {
        return new KeyEvent(KeyEventType.KeyDown, key, browserEvent);
    }

    public static up(key:Key)
    {
        return new KeyEvent(KeyEventType.KeyUp, key, null);
    }
}

export type KeyListener = CancelableListener<KeyEvent>;

export class Key
{
    private constructor(public readonly name:string,
                        public readonly code:number,
                        public readonly char:string | null,
                        public readonly isVirtual:boolean,
                        public readonly isModifier:boolean,
                        public readonly isPrintable:boolean)
    {
    }

    // seq():KeySequence
    // {
    //     return new KeySequence([this]);
    // }

    isEqualTo(key:Key):boolean
    {
        return this.code === key.code;
    }

    toString():string
    {
        return '[' + this.name + ']';
    }

    public static printable(name:string, code:number, char:string):Key
    {
        return new Key(name, code, char, false, false, true);
    }

    public static modifier(name:string, code:number):Key
    {
        return new Key(name, code, null, false, true, false);
    }

    public static virtualModifier(name:string):Key
    {
        return new Key(name, -1, null, true, true, false);
    }

    public static invalid():Key
    {
        return new Key(`unknown`, -1, null, true, true, false);
    }
}

// import {KeyMap} from "app/controllers.ui.spreadsheets/keyboard/KeyMap";

export class Keys
{
    public static ALL:Key[] = [];

    // public static from(shorthand:string):KeySequence
    // {
    //     let parts = shorthand.split('+');
    //     let keyMap = KeyMap.getInstance();
    //
    //     let keys:Key[] = [];
    //
    //     for( let part of parts )
    //         keys.push(keyMap.nameToKey(part));
    //
    //     return new KeySequence(keys);
    // }

    public static register(key:Key):Key
    {
        Keys.ALL.push(key);
        return key;
    }

    //TODO: this needs some reconsideration, commenting for now as it's not used...
    // public static fromChar(char:string):Key
    // {
    //     if( !char || char.length !== 1 )
    //         return Keys.Invalid;
    //
    //     let keyMap = KeyMap.getInstance();
    //     let keyCode = char.charCodeAt(0);
    //     return keyMap.keyCodeToKey(keyCode);
    // }

    public static isArrowKey(e:KeyEvent):boolean
    {
        let key = e.key;

        return Keys.Left.code === key.code ||
            Keys.Right.code === key.code ||
            Keys.Up.code === key.code ||
            Keys.Down.code === key.code;
    }

    public static Invalid:Key = Keys.register(Key.invalid());

    public static Cmd:Key = Keys.register(Key.virtualModifier(`cmd`));
    public static LeftCmd:Key = Keys.register(Key.modifier(`left-cmd`, KeyCode.MetaLeft));
    public static RightCmd:Key = Keys.register(Key.modifier(`right-cmd`, KeyCode.MetaRight));

    public static Shift:Key = Keys.register(Key.modifier(`shift`, KeyCode.Shift));
    public static LeftShift:Key = Keys.register(Key.virtualModifier(`left-shift`));
    public static RightShift:Key = Keys.register(Key.virtualModifier(`right-shift`));

    public static Alt:Key = Keys.register(Key.modifier(`shift`, KeyCode.Alt));
    public static LeftAlt:Key = Keys.register(Key.virtualModifier(`left-alt`));
    public static RightAlt:Key = Keys.register(Key.virtualModifier(`right-alt`));

    public static Ctl:Key = Keys.register(Key.modifier(`ctl`, KeyCode.Ctl));
    public static LeftCtl:Key = Keys.register(Key.virtualModifier(`left-ctl`));
    public static RightCtl:Key = Keys.register(Key.virtualModifier(`right-ctl`));

    public static Left:Key = Keys.register(Key.modifier(`left`, KeyCode.Left));
    public static Right:Key = Keys.register(Key.modifier(`right`, KeyCode.Right));
    public static Up:Key = Keys.register(Key.modifier(`up`, KeyCode.Up));
    public static Down:Key = Keys.register(Key.modifier(`down`, KeyCode.Down));

    public static Esc:Key = Keys.register(Key.modifier(`esc`, KeyCode.Esc));
    public static Enter:Key = Keys.register(Key.modifier(`enter`, KeyCode.Enter));
    public static Backspace:Key = Keys.register(Key.modifier(`backspace`, KeyCode.Backspace));
    public static Delete:Key = Keys.register(Key.modifier(`delete`, KeyCode.Delete));
    public static Tab:Key = Keys.register(Key.modifier(`tab`, KeyCode.Tab));

    public static Space:Key = Keys.register(Key.modifier(`space`, KeyCode.Space));

    public static F2:Key = Keys.register(Key.modifier(`f2`, KeyCode.F2));
}

export class KeyMap
{
    private static _SINGLETON = new KeyMap();

    private _codeToKey:{} = {};
    private _nameToKey:{} = {};

    constructor()
    {
        this.init();
    }

    private init():void
    {
        // 0-9
        for( let i = 48; i < 58; i++ )
        {
            let name = String.fromCharCode(i);
            let key = Key.printable(name, i, name);

            this._addMapping(key);
        }

        // A-Z
        for( let i = 65; i <= 90; i++ )
        {
            let name = String.fromCharCode(i);
            let nameLower = name.toLowerCase();

            let upperKey = Key.printable(name, i, name);
            let lowerKey = Key.printable(nameLower, i, nameLower);

            this._addMapping(upperKey);
            this._addMapping(lowerKey);
        }

        this._addMapping(Key.printable('+', 187, '+'));
        this._addMapping(Key.printable('=', 187, '='));

        this._addMapping(Key.printable('<', 188, '<'));
        this._addMapping(Key.printable(',', 188, ','));

        this._addMapping(Key.printable('_', 189, '-'));
        this._addMapping(Key.printable('-', 189, '-'));

        this._addMapping(Key.printable('>', 190, '.'));
        this._addMapping(Key.printable('.', 190, '.'));

        this._addMapping(Key.printable('?', 191, '?'));
        this._addMapping(Key.printable('/', 191, '/'));

        this._addMapping(Key.printable('~', 192, '~'));
        this._addMapping(Key.printable('`', 192, '`'));

        // well-known keys
        for( let key of Keys.ALL )
            this._addMapping(key);
    }

    public static getInstance()
    {
        return KeyMap._SINGLETON;
    }

    public nameToKey(name:string):Key
    {
        let key:Key = this._nameToKey[name];

        if( !key )
            key = Keys.Invalid;

        return key;
    }

    public keyEventToChar(event:React.KeyboardEvent<any>):string
    {
        //todo: determine full set of keys, that have charCode set, but still aren't printable (like 'Delete')
        if( event.charCode == KeyCode.Delete )
            return null;

        if( event.charCode )
            return String.fromCharCode(event.charCode);

        let keyByName:Key = this._nameToKey[event.key];
        if( keyByName )
            return keyByName.char;

        let keyByCode:Key = this._codeToKey[event.keyCode];

        if( keyByCode )
            return keyByCode.char;

        return null;
    }

    private _addMapping(key:Key):void
    {
        let {name, code} = key;

        this._nameToKey[name] = key;
        this._codeToKey[code] = key;
    }
}
